import React, { CSSProperties, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import _get from 'lodash/get';

import Flex from '@cobalt/react-flex';
import Box from '@cobalt/react-box';
import Label from '@cobalt/react-label';
import Link from '@cobalt/react-link';
import { Text } from '@cobalt/react-typography';
import Required from '@cobalt/react-required';
import Tag from '@cobalt/react-tag';

import Feedback from './Feedback';
import { RenderProps } from './help';

interface FormItemProps {
  id?: string;
  label?: string;
  size?: 'small' | 'medium';
  extraButtonText?: string;
  name: string;
  defaultValue?: unknown;
  required?: boolean | string;
  requiredText?: string;
  width?: string;
  style?: CSSProperties;
  rules?: RegisterOptions;
  extraButtonCb?: () => void;
  render: (props: RenderProps) => ReactElement;
}

export default function FormItem(props: FormItemProps) {
  const [t] = useTranslation();

  const {
    id,
    label,
    size,
    extraButtonText,
    name,
    defaultValue,
    required,
    requiredText = t('component.form.required_message', { label, defaultValue: `${label} is required` }),
    width = '100%',
    style = {},
    rules,
    extraButtonCb,
    render,
  } = props;

  const {
    control,
    formState: { errors, isDirty },
  } = useFormContext();

  const maxLength = Number(rules?.maxLength) || 0;
  const errorMsg = _get(errors, name)?.message || '';

  const [curNum, setCurNum] = useState((typeof defaultValue === 'string' && defaultValue.length) || 0);

  const showLengthTag = maxLength > 0;
  const showLinkButton = !!(extraButtonText && extraButtonCb);

  useEffect(() => {
    if (!isDirty && typeof defaultValue === 'string') {
      setCurNum(defaultValue.length);
    }
  }, [defaultValue, isDirty]);

  return (
    <Flex id={id} direction="column" paddingBottom={size === 'small' ? 3 : 4} gap="1" width={width} style={style}>
      {label && (
        <Flex alignX="space-between" alignY="center" width="100%">
          <Flex style={{ overflowX: 'hidden' }} alignY="center" grow>
            <Box style={{ overflowX: 'hidden' }} paddingRight="1">
              <Label htmlFor={name} type={errorMsg ? 'error' : undefined}>
                <Text truncated title={label} weight="medium">
                  {label}
                </Text>
              </Label>
            </Box>

            {required && <Required title="required" />}
          </Flex>

          {showLinkButton && (
            <Box paddingLeft="2">
              <Link href="#" onClick={extraButtonCb}>
                {extraButtonText}
              </Link>
            </Box>
          )}

          {showLengthTag && (
            <Box paddingLeft="2">
              <Tag size="small">{`${curNum}/${Math.floor(maxLength as number)}`}</Tag>
            </Box>
          )}
        </Flex>
      )}

      <Controller
        name={name}
        defaultValue={defaultValue}
        control={control}
        rules={{
          validate: (value) => {
            showLengthTag && setCurNum(value?.length || 0);

            if (!required) return true;

            let isValid = false;
            const type = Object.prototype.toString.call(value).slice(8, -1);

            switch (type) {
              case 'String':
                isValid = value.trim() !== '';
                break;
              case 'Boolean':
                isValid = value;
                break;
              case 'Object':
                isValid = Object.keys(value).length > 0;
                break;
              case 'Array':
                isValid = value.length > 0;
                break;
              case 'Date':
                isValid = !isNaN(value.getTime());
                break;
            }

            const requiredMsg = label ? requiredText : '';
            return isValid || (typeof required === 'string' ? required : requiredMsg);
          },
          ...rules,
        }}
        render={(props) => render({ ...props, rules })}
      />

      {errorMsg && <Feedback message={errorMsg} />}
    </Flex>
  );
}
